/**
 * Časovnik spanja — čista časovna logika (P2 §5).
 *
 * Stanje živi v `sessionStorage` (T2 §3): **ne sme preživeti zaprtja
 * zavihka**, sicer uporabnik naslednji dan odpre aplikacijo in ta se mu po
 * pol ure sama utiša.
 *
 * ⚠️ **Merilo je razlika ur, ne število tikov.** Mobilni brskalniki v ozadju
 * `setInterval` upočasnijo ali ustavijo (isti razred napake kot zamrznjeni
 * časovnik kaskade v fazi 3). Zato stanje hrani **absoluten** `endsAt` in
 * preostanek se vsakič izračuna iz trenutne ure — ne odšteva se korak po korak.
 */

/** Kdaj se sproži; absolutni epoch ms, ne preostali čas (glej zgoraj). */
export interface SleepState {
  endsAt: number;
}

/** Prednastavitve v minutah (P2 §5.1). */
export const SLEEP_PRESETS_MIN: readonly number[] = [15, 30, 45, 60, 90, 120];

/** Postopno utišanje zadnjih 30 s (P2 §5.8). */
export const SLEEP_FADE_MS = 30_000;

/** Korak ±5 min (P2 §5.4). */
export const SLEEP_NUDGE_MS = 5 * 60_000;

/** Spodnja meja po krajšanju: pod minuto časovnik nima pomena. */
export const SLEEP_MIN_MS = 60_000;

/** Po kolikšnem neprekinjenem premoru brez predvajanja se sam prekliče (P2 §5.6). */
export const SLEEP_IDLE_CANCEL_MS = 5 * 60_000;

export function sleepFromDuration(durationMs: number, now: number): SleepState {
  return { endsAt: now + Math.max(SLEEP_MIN_MS, Math.round(durationMs)) };
}

export function remainingMs(state: SleepState, now: number): number {
  return Math.max(0, state.endsAt - now);
}

export function isExpired(state: SleepState, now: number): boolean {
  return remainingMs(state, now) <= 0;
}

/**
 * Faktor glasnosti med postopnim utišanjem (P2 §5.8).
 *
 * 1 zunaj zadnjih 30 s; nato linearno proti 0. Množi se z uporabnikovo
 * glasnostjo — ne nadomesti je, ker po preklicu mora zvok znova narasti na
 * točno tisto, kar je bilo.
 */
export function fadeScale(state: SleepState, now: number): number {
  const remaining = remainingMs(state, now);
  if (remaining >= SLEEP_FADE_MS) return 1;
  return Math.max(0, remaining / SLEEP_FADE_MS);
}

/**
 * Podaljšaj ali skrajšaj za `deltaMs` (P2 §5.4).
 *
 * Deluje na **preostanku**, ne na izvirnem trajanju: +5 min pomeni pet minut
 * več od zdaj, ne od začetka. Spodnja meja je minuta — krajšanje pod njo bi
 * časovnik sprožilo skoraj takoj in bi bilo videti kot napaka.
 */
export function nudge(state: SleepState, deltaMs: number, now: number): SleepState {
  const next = Math.max(SLEEP_MIN_MS, remainingMs(state, now) + deltaMs);
  return { endsAt: now + next };
}

/**
 * Stanje iz shrambe. `null`, kadar ga ni, je pokvarjeno ali je že poteklo —
 * potekli časovnik ob ponovnem odprtju ne sme obveljati.
 */
export function parseSleep(raw: unknown, now: number): SleepState | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const endsAt = (raw as { endsAt?: unknown }).endsAt;
  if (typeof endsAt !== 'number' || endsAt <= now) return null;
  return { endsAt };
}
